const BaseAnnotationTool = cornerstoneTools.importInternal('base/BaseAnnotationTool');
const draw = cornerstoneTools.importInternal('drawing/draw');
const drawLine = cornerstoneTools.importInternal('drawing/drawLine');
const getNewContext = cornerstoneTools.importInternal('drawing/getNewContext');
const lengthCursor = cornerstoneTools.importInternal('cursors/lengthCursor');
const throttle = cornerstoneTools.importInternal('util/throttle');

// State
const addToolState = cornerstoneTools.addToolState;
const removeToolState = cornerstoneTools.removeToolState;
const getToolState = cornerstoneTools.getToolState;

const toolStyle = cornerstoneTools.toolStyle;
const toolColors = cornerstoneTools.toolColors;

const setShadow = cornerstoneTools.importInternal('drawing/setShadow');
const lineSegDistance = cornerstoneTools.importInternal('util/lineSegDistance');
const getPixelSpacing = cornerstoneTools.importInternal('util/getPixelSpacing');

/**
 * @public
 * @class HLineTool
 * @memberof Tools.Annotation
 * @classdesc Tool for create horizontal lines.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class HLineTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: 'HLine',
      supportedInteractionTypes: ['Mouse', 'Touch'],
      svgCursor: lengthCursor,
      configuration: {
        renderDashed: false,
      },
    };

    super(props, defaultProps);

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 110);
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      logger.error(
        `required eventData not supplied to tool ${this.name}'s createNewMeasurement`
      );

      return;
    }

    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        end: {
          x: eventData.image.columns/2,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
      },
    };
  }

  /**
   *
   *
   * @param {*} element
   * @param {*} data
   * @param {*} coords
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    const hasEndHandle = data && data.handles && data.handles.end;
    const validParameters = hasEndHandle;


    if (!validParameters) {
      logger.warn(
        `invalid parameters supplied to tool ${this.name}'s pointNearTool`
      );

      return false;
    }

    if (data.visible === false) {
      return false;
    }

    return (
      lineSegDistance(element, { x: 0, y: data.handles.end.y }, { x:data.handles.end.x*2, y: data.handles.end.y }, coords) <
      20
    );
  }

  updateCachedStats(image, element, data) {
    data.invalidated = false;
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    const renderDashed = this.configuration.renderDashed;
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    const { image, element } = eventData;
    const { rowPixelSpacing, colPixelSpacing } = getPixelSpacing(image);

    const lineWidth = toolStyle.getToolWidth();
    const lineDash = cornerstoneTools.getModule('globalConfiguration').configuration.lineDash;

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false) {
        continue;
      }

      data.handles.end.y = Math.max(Math.min(data.handles.end.y, image.height), 0);
      data.handles.end.x = image.columns/2;

      draw(context, context => {
        // Configurable shadow
        setShadow(context, this.configuration);

        const color = toolColors.getColorIfActive(data);

        const lineOptions = { color };

        if (renderDashed) {
          lineOptions.lineDash = lineDash;
        }

        // Draw the measurement line
        drawLine(
          context,
          element,
          { x: 0, y: data.handles.end.y },
          { x: image.columns, y: data.handles.end.y },
          lineOptions
        );

      });
    }

  }

}
